import { useEffect, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { api, apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, Camera, Trash2, PenLine, Maximize2, X } from "lucide-react";
import { fmtDate } from "@/lib/constants";
import { resizeImage } from "@/lib/image";
import { toast } from "sonner";

const EMBLEM = "/emblem.png";
const ASSOCIAZIONE = "Associazione Nazionale Guardie Ecozoofile ODV";
const RETRO_TESTO = "Il titolare della presente tessera è un Pubblico Ufficiale con funzione Agente di Polizia Giudiziaria in base all'Art. 55 e 57 C.P.P. quale Guardia Zoofila dell'A.N.G.E. come previsto dalla Legge 189/2004 Art. 6 Comma 2, nominato dal Prefetto di Napoli.";
const RETRO_TESTO_OPERATORE = "Il titolare della presente tessera è un Volontario dell'A.N.G.E. - Associazione Nazionale Guardie Ecozoofile ODV, impegnato in attività di sensibilizzazione, supporto e tutela degli animali e dell'ambiente.";

function bandColor(grado) {
  const s = (grado || "").toLowerCase();
  if (s.includes("dirigente")) return "#B91C1C"; // rosso: Dirigente e Dirigente Superiore
  if (s.includes("com.")) return "#1D4ED8"; // blu: Com. Territoriale e Com. Distaccamento
  if (s.includes("operatore")) return "#EAB308"; // giallo: Operatore
  return "#166534"; // verde: guardie (Sottufficiale, Agente, Agente scelto, altro)
}

function bandTextColor(grado) {
  return (grado || "").toLowerCase().includes("operatore") ? "#14382C" : "#ffffff";
}

export default function Tesserino({ g, canEdit = false }) {
  const qc = useQueryClient();
  const [qr, setQr] = useState("");
  const fotoRef = useRef(null);
  const firmaRef = useRef(null);
  const initials = (g.nome?.[0] || "") + (g.cognome?.[0] || "");
  const band = bandColor(g.ruolo_grado);
  const bandText = bandTextColor(g.ruolo_grado);
  const isOperatore = (g.ruolo_grado || "").toLowerCase().includes("operatore");

  const { data: settings } = useQuery({
    queryKey: ["tesserino-settings"],
    queryFn: async () => (await api.get("/settings/tesserino")).data,
  });
  const firma = settings?.firma_presidente;
  const nomePresidente = settings?.nome_presidente;

  useEffect(() => {
    const url = `${window.location.origin}/verifica/${g.id}`;
    QRCode.toDataURL(url, { margin: 1, width: 240 }).then(setQr).catch(() => {});
  }, [g.id]);

  const handlePrint = () => {
    document.body.classList.add("tess-print");
    window.print();
  };

  useEffect(() => {
    const cleanup = () => document.body.classList.remove("tess-print");
    window.addEventListener("afterprint", cleanup);
    return () => window.removeEventListener("afterprint", cleanup);
  }, []);

  const onFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeImage(file, 400, 520, 0.85);
      await api.post(`/guardie/${g.id}/foto`, { foto: dataUrl });
      await qc.invalidateQueries({ queryKey: ["guardia", g.id] });
      toast.success("Foto aggiornata");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Errore caricamento foto"); }
  };

  const removeFoto = async () => {
    try {
      await api.delete(`/guardie/${g.id}/foto`);
      await qc.invalidateQueries({ queryKey: ["guardia", g.id] });
      toast.success("Foto rimossa");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const onFirma = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeImage(file, 500, 200, 0.9);
      await api.put("/settings/tesserino", { firma_presidente: dataUrl });
      await qc.invalidateQueries({ queryKey: ["tesserino-settings"] });
      toast.success("Firma del Presidente aggiornata");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Errore caricamento firma"); }
  };

  const [fullscreen, setFullscreen] = useState(false);

  const Row = ({ l, v, mono }) => (
    <div className="flex items-center justify-between border-b border-dotted border-slate-200 py-0.5 text-xs">
      <span className="uppercase tracking-wide text-slate-400">{l}</span>
      <span className={`font-semibold text-slate-900 ${mono ? "font-mono-code" : ""}`}>{v || "—"}</span>
    </div>
  );

  const cardsJsx = (
    <>
      <p className="mx-auto mb-1.5 w-full max-w-md text-xs font-semibold uppercase tracking-wide text-slate-400 tess-noprint">Fronte</p>
      <div className="tess-front tess-card mx-auto w-full max-w-md overflow-hidden rounded-xl border-2 border-[#14382C] bg-white shadow-md">
        <div className="flex items-center gap-3 bg-[#14382C] px-4 py-2 text-white">
          <img src={EMBLEM} alt="Emblema associazione" className="h-11 w-auto object-contain" />
          <div>
            <p className="text-[11px] font-bold uppercase leading-tight tracking-wide">{ASSOCIAZIONE}</p>
            <p className="text-[10px] text-emerald-100/80">Tesserino di riconoscimento</p>
          </div>
        </div>
        <div className="flex gap-3 p-4">
          <div className="flex h-28 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#14382C] bg-slate-50 font-heading text-3xl font-extrabold text-[#14382C]">
            {g.foto ? <img src={g.foto} alt="Foto guardia" className="h-full w-full object-cover" /> : initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="mb-1 font-heading text-base font-extrabold leading-tight text-[#14382C]">{g.cognome} {g.nome}</p>
            <Row l="Ruolo/Grado" v={g.ruolo_grado} />
            <Row l="Matricola" v={g.matricola} />
            <Row l="Tessera" v={g.numero_tessera} />
            <Row l="Cod. Fiscale" v={g.codice_fiscale} mono />
            <Row l="Distacc." v={g.distaccamento} />
            <Row l="N. Decreto" v={g.numero_decreto} />
            <Row l="Scadenza" v={fmtDate(g.scadenza_decreto)} />
          </div>
          <div className="flex w-16 flex-shrink-0 flex-col items-center justify-between">
            <div className="text-center">
              {qr && <img src={qr} alt="QR verifica" className="h-16 w-16" data-testid="tesserino-qr" />}
              <p className="mt-0.5 text-[8px] uppercase leading-tight text-slate-400">Scansiona<br />per verifica</p>
            </div>
            <div className="flex flex-col items-center">
              {firma
                ? <img src={firma} alt="Firma Presidente" className="h-8 object-contain" />
                : <div className="h-8 w-16 border-b border-slate-400" />}
              <p className="mt-0.5 text-center text-[8px] uppercase leading-tight text-slate-500">Il Presidente{nomePresidente ? <><br />{nomePresidente}</> : null}</p>
            </div>
          </div>
        </div>
        <div style={{ backgroundColor: band, color: bandText }} className="px-4 py-1.5 text-center text-[11px] font-bold uppercase tracking-wide">
          {isOperatore ? "Volontario" : "Guardia Zoofila · Agente di Polizia Giudiziaria"}
        </div>
      </div>

      <p className="mx-auto mb-1.5 mt-6 w-full max-w-md text-xs font-semibold uppercase tracking-wide text-slate-400 tess-noprint">Retro</p>
      <div className="tess-card mx-auto w-full max-w-md overflow-hidden rounded-xl border-2 border-[#14382C] bg-white shadow-md" data-testid="tesserino-retro">
        <div className="flex items-center justify-center gap-3 bg-[#14382C] px-4 py-2 text-center text-white">
          <img src={EMBLEM} alt="Emblema associazione" className="h-10 w-auto object-contain" />
          <div>
            <p className="text-[11px] font-bold uppercase leading-tight tracking-wide">Associazione Nazionale Guardie Ecozoofile</p>
            <p className="text-[10px] text-emerald-100/80">Servizio di Vigilanza Zoofila e Ambientale</p>
          </div>
        </div>
        <div className="p-4">
          <p className="border-b border-dotted border-slate-200 pb-2 text-center text-xs font-semibold text-slate-700">Sede Legale Via Pia 50 Napoli · C.F. 95248830630</p>
          <p className="mt-3 text-justify text-[13px] leading-relaxed text-slate-800">{isOperatore ? RETRO_TESTO_OPERATORE : RETRO_TESTO}</p>
        </div>
        <div style={{ backgroundColor: band, color: bandText }} className="px-4 py-1.5 text-center text-[11px] font-bold uppercase tracking-wide">
          {isOperatore ? "A.N.G.E." : "A.N.G.E. · Legge 189/2004 Art. 6 Comma 2"}
        </div>
      </div>
    </>
  );

  return (
    <>
    <Card className="border-slate-200 shadow-sm" data-testid="tesserino-card">
      <CardContent className="p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-lg font-bold text-slate-900">Documenti personali · Tesserino</h2>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <>
                <input ref={fotoRef} type="file" accept="image/*" className="hidden" onChange={onFoto} data-testid="foto-input" />
                <Button size="sm" variant="outline" onClick={() => fotoRef.current?.click()} data-testid="upload-foto">
                  <Camera className="mr-2 h-4 w-4" /> {g.foto ? "Cambia foto" : "Carica foto"}
                </Button>
                {g.foto && (
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={removeFoto} data-testid="remove-foto">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                <input ref={firmaRef} type="file" accept="image/*" className="hidden" onChange={onFirma} data-testid="firma-input" />
                <Button size="sm" variant="outline" onClick={() => firmaRef.current?.click()} data-testid="upload-firma">
                  <PenLine className="mr-2 h-4 w-4" /> Firma Presidente
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={() => setFullscreen(true)} data-testid="fullscreen-tesserino" className="lg:hidden">
              <Maximize2 className="mr-2 h-4 w-4" /> Schermo intero
            </Button>
            <Button size="sm" onClick={handlePrint} data-testid="print-tesserino" className="bg-[#14382C] hover:bg-[#0E281F]">
              <Printer className="mr-2 h-4 w-4" /> Stampa / Scarica PDF
            </Button>
          </div>
        </div>

        <div id="tesserino-print-area">{cardsJsx}</div>

        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 pt-4 tess-noprint" data-testid="tesserino-legenda">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Legenda colori banda</span>
          {[
            { c: "#B91C1C", l: "Dirigenti", t: "#fff" },
            { c: "#1D4ED8", l: "Comandanti", t: "#fff" },
            { c: "#166534", l: "Guardie", t: "#fff" },
            { c: "#EAB308", l: "Operatore", t: "#14382C" },
          ].map((x) => (
            <span key={x.l} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className="inline-block h-3.5 w-6 rounded-sm border border-black/10" style={{ backgroundColor: x.c }} />
              {x.l}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>

    {fullscreen && (
      <div className="fixed inset-0 z-[60] overflow-auto bg-slate-900/95 p-4 tess-noprint" data-testid="tesserino-fullscreen" onClick={() => setFullscreen(false)}>
        <div className="mx-auto flex max-w-2xl flex-col items-center" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setFullscreen(false)} data-testid="close-fullscreen" className="mb-4 self-end rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20">
            <X className="mr-1 inline h-4 w-4" /> Chiudi
          </button>
          {cardsJsx}
        </div>
      </div>
    )}
    </>
  );
}
